<template>
  <div :class="componentClasses">
    <PriorityIcon
      v-if="!isEditing"
      :priority="task.priority"
    />

    <Stack
      v-if="isEditing"
      class="form"
      gap="2"
    >
      <label class="form-label">Name</label>
      <TextInput
        v-model="editableName"
      />

      <label class="form-label">Description</label>
      <textarea
        v-model="editableDescription"
        class="stk-input"
      />

      <label class="form-label">Priority</label>
      <div>
        <div class="stk-select">
          <select
            v-model="editablePriority"
            class="is-capitalized"
          >
            <option
              v-for="(option) in priorityOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <Icon />
        </div>
      </div>

      <label class="form-label">Due Date</label>
      <div>
        <TextInput
          v-model="editableDueDate"
          type="date"
        />
      </div>

      <label class="form-label">Prospect/Client</label>
      <div>
        <!--
        <div class="stk-select">
          <select
            v-model="editableEntityExternalId"
            class="is-capitalized"
          >
            <option
              v-for="(entity) in taskStore.tasks.allEntities"
              :key="entity.external_id"
              :value="entity.external_id"
            >
              {{ entity.name }}
            </option>
          </select>
          <Icon />
        </div>
        -->
        <AutocompleteWithFetch
          v-slot="{ item }"
          endpoint="/api/v1/entities/get_all/"
          results-field="items"
          :multiple="false"
          :init-selected="task?.entity ? [task.entity] : []"
          @selection-change="handleEntitySelectionChange"
        >
          <div>
            <!-- Custom html for list items -->
            <p>{{ item.name }}</p>
          </div>
        </AutocompleteWithFetch>
      </div>

      <label class="form-label">Assignee</label>
      <!--
      <div class="radios">
        <label
          v-for="advisor in taskStore.tasks.allAdvisors"
          :key="advisor.id"
        >
          <input
            v-model="editableAssignees"
            type="checkbox"
            :value="advisor.id"
          >
          {{ advisor.name }}
        </label>
      </div>
      -->
      <AutocompleteWithFetch
        v-slot="{ item }"
        endpoint="/api/v1/advisors/"
        results-field="advisors"
        :multiple="true"
        :init-selected="task?.assignees ?? []"
        @selection-change="handleAssigneeSelectionChange"
      >
        <div>
          <!-- Custom html for list items -->
          <p>{{ item.name }}</p>
        </div>
      </AutocompleteWithFetch>

      <label class="form-label" />
      <Buttons gap="2">
        <Button
          variant="success"
          :disabled="isSaving"
          @click="saveChanges(task.status)"
        >
          Save changes
        </Button>

        <Button @click="cancelChanges">
          Cancel
        </Button>
      </Buttons>
    </Stack>

    <Stack
      v-else
      class="task-body"
      gap="1"
    >
      <header class="task-header">
        <Buttons
          class="tw-float-right tw-ml-2"
          gap="2"
        >
          <Icon
            v-if="variant === 'kanban'"
            class="task-grip"
            icon="fas fa-grip-vertical"
          />

          <PriorityBadge
            v-else
            :priority="task.priority"
          />

          <Button
            spacing="tight"
            size="3"
            icon-before="fas fa-edit"
            @click="isEditing = !isEditing"
          >
            Edit
          </Button>
          <Button
            v-if="task.status === 'completed'"
            class="mark-completed"
            spacing="tight"
            size="3"
            @click="saveChanges('accepted')"
          >
            Mark as active
          </Button>
          <Button
            v-else
            class="mark-completed"
            spacing="tight"
            size="3"
            icon-before="fas fa-check"
            @click="saveChanges('completed')"
          >
            Mark as completed
          </Button>
        </Buttons>

        <Title
          size="4"
        >
          {{ task.name }}
        </Title>

        <div
          class="task-description"
        >
          {{ task.description }}
        </div>
      </header>

      <footer class="task-footer">
        <div
          v-if="task.due_date"
          class="task-footer-item"
        >
          <Badge
            status="future"
            icon-before="fas fa-calendar"
          >
            {{ task.due_date }}
          </Badge>
        </div>

        <div
          v-if="task.entity"
          class="task-footer-item"
        >
          <Badge
            status="info"
            icon-before="fas fa-user"
            :to="{ name: 'entity-detail', params: { entityExternalId: task.entity.external_id } }"
          >
            {{ task.entity.name }}
          </Badge>
        </div>

        <Badges
          v-if="task.assignees.length"
          class="task-advisors"
        >
          <Badge
            status="ghost"
            class="task-advisor-label"
          >
            Assigned to
          </Badge>
          <Badge
            v-for="advisor in task.assignees"
            :key="advisor.id"
          >
            {{ advisor.name }}
          </Badge>
        </Badges>
      </footer>
    </Stack>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Badge, Badges, Button, Buttons, Icon, Stack, TextInput, Title } from '@stakeui';
import PriorityBadge from '@frontend/ui/features/tasks/components/priority/PriorityBadge.vue';
import PriorityIcon from '@frontend/ui/features/tasks/components/priority/PriorityIcon.vue';
import { useTaskStore } from '@frontend/ui/stores/taskStore.js';
import { ToastProgrammatic as Toast } from 'buefy';
import AutocompleteWithFetch from '@frontend/ui/components/utils/AutocompleteWithFetch.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  variant: {
    type: String,
    default: '',
  },
});

const taskStore = useTaskStore();

const isEditing = ref(false);
const isSaving = ref(false);

const originalTask = props.task;

const editableName = ref(originalTask.name);
const editableDescription = ref(originalTask.description);
const editablePriority = ref(originalTask.priority);
const editableDueDate = ref(originalTask.due_date);
const editableEntityExternalId = ref(originalTask.entity?.external_id ?? null);
const editableAssignees = ref(originalTask.advisors?.map(a => a.id) ?? []);

function resetEditableFields() {
  editableName.value = originalTask.name;
  editableDescription.value = originalTask.description;
  editablePriority.value = originalTask.priority;
  editableDueDate.value = originalTask.due_date;
  editableEntityExternalId.value = originalTask.entity?.external_id ?? null;
  editableAssignees.value = originalTask.advisors?.map(a => a.id) ?? [];
}

const priorityOptions = [
  { label: 'critical', value: 1 },
  { label: 'high', value: 2 },
  { label: 'medium', value: 3 },
  { label: 'low', value: 4 },
];


const handleEntitySelectionChange = selected => {
  if (!selected) {
    editableEntityExternalId.value = null;
    return;
  }
  const entity = selected[0];

  if (entity) {
    editableEntityExternalId.value = entity.external_id;
  }
};


const handleAssigneeSelectionChange = selected => {
  if (!selected) {
    editableAssignees.value = [];
    return;
  }
  editableAssignees.value = selected.map(s => s.id);
};


const saveChanges = async status => {
  isSaving.value = true;

  // Fetch again if the task was marked as completed, or is about to
  const refetch = status === 'completed' || props.task.status === 'completed';

  try {
    await taskStore.saveTask({
      id: props.task.id,
      externalId: props.task.external_id,
      status,
      name: editableName.value,
      description: editableDescription.value,
      priority: editablePriority.value,
      dueDate: editableDueDate.value,
      entityExternalId: editableEntityExternalId.value,
      assigneeIds: editableAssignees.value,
    }, refetch);
    isEditing.value = false;
    Toast.open({
      message: `Task successfully updated.`,
      type: 'is-success',
    });
  } catch (e) {
    console.error('Task save failed', e);
    Toast.open({
      message: `There was an error saving the task.`,
      type: 'is-danger',
    });
  } finally {
    isSaving.value = false;
  }
};

const cancelChanges = () => {
  resetEditableFields();
  isEditing.value = false;
};

const componentClasses = computed(() => [
  'task-element',
  `priority-${props.task.priority}`,
  props.size ? `stk-size-${props.size}` : '',
  props.variant ? `variant-${props.variant}` : '',
]);
</script>

<style scoped>
.task-element {
  background-color: var(--stk-control-background-color);
  border-color: var(--stk-control-border-color);
  border-radius: 0.75em;
  border-style: solid;
  border-width: var(--stk-control-border-width);
  box-shadow: var(--stk-control-shadow);
  display: flex;
  align-items: start;
  gap: 1em;
  padding: 1em;
  overflow: hidden;
  transition: height 200ms ease, opacity 200ms ease;

  &.is-selected {
    border-color: var(--stk-primary);
    outline-color: var(--stk-primary);
    outline-width: 1px;
    outline-style: solid;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.02);
  }

  &.priority-critical {
    border-color: var(--stk-danger-50);
    outline: 1px solid var(--stk-danger-50);
  }

  &.variant-kanban {
    margin-bottom: 1em;

    .task-description {
      display: none;
    }

    .task-footer {
      flex-direction: column;
    }

    .task-advisors {
      margin-left: 0;
      gap: 0.25em;
    }

    .task-advisor-label {
      display: none;
    }
  }
}

.task-grip {
  color: var(--stk-text-weak);
  opacity: 0.5;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.task-body {
  flex-grow: 1;
}

.task-description {
  font-size: 0.875em;
}

.task-footer {
  margin-top: 0.5em;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

.task-advisors {
  margin-left: auto;
}

.task-advisor-label {
  padding-left: 0;
  padding-right: 0;
}

.form {
  display: grid;
  flex-grow: 1;
  grid-template-columns: 8em 1fr;
  gap: 0.5em 1em;
}

.form-label {
  padding-top: 0.5em;
}

.radios {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5em 1em;

  > label {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5em;
  }
}

.mark-completed {
  :deep(.stk-icon) {
    color: var(--stk-success-50);
  }
}
</style>
